/**
 * @module game/ObstacleManager
 * Tracks obstacle-induced penalties.
 *
 * Ice obstacle: slashing an ice block imposes a "sticky blade" penalty — the
 * effective slash velocity threshold rises by CONFIG.obstacle.icePenaltyVelocityScale
 * for CONFIG.obstacle.icePenaltyDuration ms, making it harder to register slashes.
 *
 * Rotten fruit: no time-based penalty; the slash simply deducts points
 * (handled in ScoreManager). ObstacleManager only tracks the ice penalty.
 */

import { CONFIG } from '../game-config.js';

/** Read-only view of active obstacle penalties. */
export interface ObstacleState {
  /** Whether the ice velocity penalty is currently active. */
  icePenaltyActive: boolean;
  /** Remaining ice penalty duration (ms). 0 when inactive. */
  iceRemaining: number;
  /**
   * Effective minimum slash velocity threshold (px/s).
   * During ice penalty: CONFIG.slash.minVelocity × CONFIG.obstacle.icePenaltyVelocityScale.
   * Otherwise:          CONFIG.slash.minVelocity.
   */
  effectiveMinVelocity: number;
}

/**
 * Manages ice obstacle penalty timing.
 *
 * Call {@link ObstacleManager.hitIce} when the blade contacts an ice obstacle.
 * Call {@link ObstacleManager.tick} each frame with the delta to count down.
 * Read {@link ObstacleManager.state} for the current penalty state.
 *
 * One instance per game session; call {@link ObstacleManager.reset} on restart.
 */
export class ObstacleManager {
  /** Remaining ice penalty time (ms). */
  private _iceRemaining = 0;

  /**
   * Activates (or restarts) the ice penalty timer.
   * Hitting a second ice block while already penalised resets the timer.
   */
  hitIce(): void {
    this._iceRemaining = CONFIG.obstacle.icePenaltyDuration;
  }

  /**
   * Advances the ice penalty timer by `deltaMs`.
   *
   * @param deltaMs - Frame delta in milliseconds.
   */
  tick(deltaMs: number): void {
    this._iceRemaining = Math.max(0, this._iceRemaining - deltaMs);
  }

  /** Whether the ice penalty is currently active. */
  get icePenaltyActive(): boolean {
    return this._iceRemaining > 0;
  }

  /**
   * Effective minimum slash velocity threshold for the gesture engine.
   * During ice penalty the threshold is scaled up, making slashes harder to register.
   */
  get effectiveMinVelocity(): number {
    if (this._iceRemaining > 0) {
      return CONFIG.slash.minVelocity * CONFIG.obstacle.icePenaltyVelocityScale;
    }
    return CONFIG.slash.minVelocity;
  }

  /** Read-only state snapshot — passed to the gesture engine each frame. */
  get state(): ObstacleState {
    return {
      icePenaltyActive: this._iceRemaining > 0,
      iceRemaining: this._iceRemaining,
      effectiveMinVelocity: this.effectiveMinVelocity,
    };
  }

  /** Resets all penalty state for a new game session. */
  reset(): void {
    this._iceRemaining = 0;
  }
}
